/* eslint-disable no-bitwise */
/* global globalThis */
/**
 * WLT1 — hand-ported from
 * weallat_data_transit_encryption/clients/typescript/src/wlt1.ts
 *
 * Mechanical strip: TypeScript type annotations, interfaces, generics and
 * `as` casts removed; every constant, byte order, regex, base64url handling,
 * AAD composition, HKDF `info` string and path-normalisation rule preserved
 * verbatim. Do not change any of those without reading WIRE_FORMAT.md §16 —
 * they are frozen contract with the Python server.
 *
 * This port must be re-verified against ../__vectors__/wlt1.json and
 * paths.json on every edit; see runVectors.js.
 */

export const MAGIC = 'WLT1'
export const NONCE_LENGTH = 12
export const TAG_LENGTH_BITS = 128
export const TAG_LENGTH = 16

const KID_PATTERN =
    /^(?<slug>[a-z][a-z0-9-]{1,30})-v(?<version>[0-9]{1,4})(?:\+s:(?<session>[A-Za-z0-9_-]{1,64}))?$/

const BASE64URL_PATTERN = /^[A-Za-z0-9_-]*={0,2}$/

export class TransportError extends Error {
    constructor(code, message) {
        super(message ?? code)
        this.name = 'TransportError'
        this.code = code
    }
}

export const ERR_MALFORMED = 'transport_malformed_envelope'
export const ERR_DECRYPT_FAILED = 'transport_decrypt_failed'
export const ERR_UNKNOWN_KID = 'transport_unknown_kid'

// ---------------------------------------------------------------------------
// base64url — WIRE_FORMAT.md §4
// ---------------------------------------------------------------------------

const B64URL_ALPHABET =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

export function b64urlEncode(bytes) {
    let out = ''
    for (let i = 0; i < bytes.length; i += 3) {
        const a = bytes[i]
        const b = i + 1 < bytes.length ? bytes[i + 1] : undefined
        const c = i + 2 < bytes.length ? bytes[i + 2] : undefined

        out += B64URL_ALPHABET[a >> 2]
        out += B64URL_ALPHABET[((a & 0x03) << 4) | ((b ?? 0) >> 4)]
        if (b === undefined) break
        out += B64URL_ALPHABET[((b & 0x0f) << 2) | ((c ?? 0) >> 6)]
        if (c === undefined) break
        out += B64URL_ALPHABET[c & 0x3f]
    }
    return out
}

export function b64urlDecode(text) {
    if (!BASE64URL_PATTERN.test(text)) {
        throw new TransportError(ERR_MALFORMED, 'not base64url')
    }
    const stripped = text.replace(/=+$/, '')
    const out = new Uint8Array(Math.floor((stripped.length * 6) / 8))
    let bits = 0
    let accumulator = 0
    let index = 0

    for (const character of stripped) {
        const value = B64URL_ALPHABET.indexOf(character)
        if (value < 0) throw new TransportError(ERR_MALFORMED, 'not base64url')
        accumulator = (accumulator << 6) | value
        bits += 6
        if (bits >= 8) {
            bits -= 8
            out[index++] = (accumulator >> bits) & 0xff
        }
    }
    return out.subarray(0, index)
}

// ---------------------------------------------------------------------------
// kid — WIRE_FORMAT.md §5
// ---------------------------------------------------------------------------

export function parseKid(kid) {
    const match = KID_PATTERN.exec(kid)
    if (!match?.groups) {
        throw new TransportError(ERR_MALFORMED, 'bad kid')
    }
    return {
        raw: kid,
        slug: match.groups.slug,
        version: `v${match.groups.version}`,
        session: match.groups.session,
    }
}

// ---------------------------------------------------------------------------
// AAD — WIRE_FORMAT.md §7 and §8
// ---------------------------------------------------------------------------

function utf8EncodeImpl(text) {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text)

    const out = []
    for (let i = 0; i < text.length; i++) {
        let point = text.charCodeAt(i)
        if (point >= 0xd800 && point <= 0xdbff && i + 1 < text.length) {
            const low = text.charCodeAt(i + 1)
            if (low >= 0xdc00 && low <= 0xdfff) {
                point = (point - 0xd800) * 0x400 + (low - 0xdc00) + 0x10000
                i++
            }
        }
        if (point < 0x80) {
            out.push(point)
        } else if (point < 0x800) {
            out.push(0xc0 | (point >> 6), 0x80 | (point & 0x3f))
        } else if (point < 0x10000) {
            out.push(0xe0 | (point >> 12), 0x80 | ((point >> 6) & 0x3f), 0x80 | (point & 0x3f))
        } else {
            out.push(
                0xf0 | (point >> 18),
                0x80 | ((point >> 12) & 0x3f),
                0x80 | ((point >> 6) & 0x3f),
                0x80 | (point & 0x3f),
            )
        }
    }
    return new Uint8Array(out)
}

function utf8DecodeImpl(bytes) {
    if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(bytes)

    let out = ''
    for (let i = 0; i < bytes.length; ) {
        const byte = bytes[i]
        let point
        let size

        if (byte < 0x80) {
            point = byte
            size = 1
        } else if ((byte & 0xe0) === 0xc0) {
            point = byte & 0x1f
            size = 2
        } else if ((byte & 0xf0) === 0xe0) {
            point = byte & 0x0f
            size = 3
        } else if ((byte & 0xf8) === 0xf0) {
            point = byte & 0x07
            size = 4
        } else {
            out += '�'
            i += 1
            continue
        }

        if (i + size > bytes.length) {
            out += '�'
            break
        }
        for (let k = 1; k < size; k++) {
            const next = bytes[i + k]
            if ((next & 0xc0) !== 0x80) {
                point = -1
                break
            }
            point = (point << 6) | (next & 0x3f)
        }
        i += size

        if (point < 0) {
            out += '�'
        } else if (point > 0xffff) {
            point -= 0x10000
            out += String.fromCharCode(0xd800 + (point >> 10), 0xdc00 + (point & 0x3ff))
        } else {
            out += String.fromCharCode(point)
        }
    }
    return out
}

export const utf8Encode = utf8EncodeImpl
export const utf8Decode = utf8DecodeImpl

function asBytes(component) {
    return typeof component === 'string' ? utf8Encode(component) : component
}

export function composeAad(components) {
    const encoded = components.map(asBytes)
    const total = encoded.reduce((sum, part) => sum + 4 + part.length, 0)
    const out = new Uint8Array(total)
    const view = new DataView(out.buffer)
    let offset = 0
    for (const part of encoded) {
        view.setUint32(offset, part.length, false) // false = big-endian
        offset += 4
        out.set(part, offset)
        offset += part.length
    }
    return out
}

export function requestAad(kid, method, path, timestamp, messageId) {
    return composeAad([
        MAGIC,
        kid,
        method.toUpperCase(),
        path,
        String(timestamp),
        messageId,
    ])
}

export function responseAad(kid, method, path, timestamp, messageId, requestMessageId) {
    return composeAad([
        MAGIC,
        kid,
        method.toUpperCase(),
        path,
        String(timestamp),
        messageId,
        requestMessageId,
    ])
}

// ---------------------------------------------------------------------------
// Path normalisation — WIRE_FORMAT.md §9
// ---------------------------------------------------------------------------

export function unquote(text) {
    if (!text.includes('%')) return text

    const bytes = []
    for (let i = 0; i < text.length; ) {
        if (text[i] === '%' && /^[0-9A-Fa-f]{2}$/.test(text.slice(i + 1, i + 3))) {
            bytes.push(parseInt(text.slice(i + 1, i + 3), 16))
            i += 3
        } else {
            for (const byte of utf8Encode(text[i])) bytes.push(byte)
            i += 1
        }
    }
    return utf8Decode(new Uint8Array(bytes))
}

export function stripBasePath(path, basePath) {
    if (!basePath) return path
    const base = `/${basePath.replace(/^\/+|\/+$/g, '')}`
    if (base === '/') return path
    if (path === base) return '/'
    if (path.startsWith(`${base}/`)) return path.slice(base.length)
    return path
}

export function normalisePath(target, basePath = '') {
    let path = target

    if (path.includes('://')) {
        const afterScheme = path.slice(path.indexOf('://') + 3)
        const slash = afterScheme.indexOf('/')
        path = slash === -1 ? '/' : afterScheme.slice(slash)
    }

    path = path.split('#')[0].split('?')[0] || '/'
    path = stripBasePath(path, basePath)
    path = unquote(path)

    if (!path.startsWith('/')) path = `/${path}`
    if (path !== '/' && path.endsWith('/')) path = path.slice(0, -1)
    return path || '/'
}

// ---------------------------------------------------------------------------
// Keys — WIRE_FORMAT.md §10
// ---------------------------------------------------------------------------

let injectedCrypto = null

export function setCryptoProvider(provider) {
    injectedCrypto = provider
}

function getCrypto() {
    const provider = injectedCrypto ?? globalThis.crypto
    if (!provider?.subtle) {
        throw new TransportError(
            ERR_MALFORMED,
            'WebCrypto is unavailable. In a browser this means the page is not a secure ' +
                'context — crypto.subtle needs HTTPS or localhost.',
        )
    }
    return provider
}

function getSubtle() {
    return getCrypto().subtle
}

export function randomNonce() {
    return getCrypto().getRandomValues(new Uint8Array(NONCE_LENGTH))
}

function consumerInfo(slug, version, direction) {
    return utf8Encode(
        `weallat-transport/v1|consumer|${slug}|${version}|${direction}`,
    )
}

export async function deriveKeys(kid, master, salt) {
    const parsed = parseKid(kid)
    if (master.length !== 32) throw new TransportError(ERR_MALFORMED, 'master must be 32 bytes')
    if (salt.length !== 16) throw new TransportError(ERR_MALFORMED, 'salt must be 16 bytes')

    const ikm = await getSubtle().importKey('raw', master, 'HKDF', false, ['deriveBits'])

    const derive = async direction => {
        const bits = await getSubtle().deriveBits(
            {
                name: 'HKDF',
                hash: 'SHA-256',
                salt,
                info: consumerInfo(parsed.slug, parsed.version, direction),
            },
            ikm,
            256,
        )
        return getSubtle().importKey('raw', bits, 'AES-GCM', false, ['encrypt', 'decrypt'])
    }

    return {
        kid,
        requestKey: await derive('request'),
        responseKey: await derive('response'),
    }
}

export async function importDerivedKeys(kid, requestKey, responseKey) {
    parseKid(kid)
    for (const [name, key] of [['request', requestKey], ['response', responseKey]]) {
        if (key.length !== 32) {
            throw new TransportError(ERR_MALFORMED, `${name} key must be 32 bytes`)
        }
    }
    const subtle = getSubtle()
    return {
        kid,
        requestKey: await subtle.importKey('raw', requestKey, 'AES-GCM', false, [
            'encrypt',
            'decrypt',
        ]),
        responseKey: await subtle.importKey('raw', responseKey, 'AES-GCM', false, [
            'encrypt',
            'decrypt',
        ]),
    }
}

export function base64Decode(text) {
    const standard = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
    const stripped = text.replace(/[\r\n\s]/g, '').replace(/=+$/, '')
    const out = new Uint8Array(Math.floor((stripped.length * 6) / 8))
    let bits = 0
    let accumulator = 0
    let index = 0

    for (const character of stripped) {
        const value = standard.indexOf(character)
        if (value < 0) throw new TransportError(ERR_MALFORMED, 'not base64')
        accumulator = (accumulator << 6) | value
        bits += 6
        if (bits >= 8) {
            bits -= 8
            out[index++] = (accumulator >> bits) & 0xff
        }
    }
    return out.subarray(0, index)
}

// ---------------------------------------------------------------------------
// Envelope — WIRE_FORMAT.md §3 and §11
// ---------------------------------------------------------------------------

export function decodeEnvelope(envelope) {
    const first = envelope.indexOf('.')
    const second = envelope.indexOf('.', first + 1)
    const third = envelope.indexOf('.', second + 1)
    if (first < 0 || second < 0 || third < 0) {
        throw new TransportError(ERR_MALFORMED, 'segment count')
    }

    if (envelope.slice(0, first) !== MAGIC) {
        throw new TransportError(ERR_MALFORMED, 'magic')
    }

    const kid = parseKid(envelope.slice(first + 1, second))
    const nonce = b64urlDecode(envelope.slice(second + 1, third))
    const payload = b64urlDecode(envelope.slice(third + 1))

    if (nonce.length !== NONCE_LENGTH) throw new TransportError(ERR_MALFORMED, 'nonce length')
    if (payload.length < TAG_LENGTH) throw new TransportError(ERR_MALFORMED, 'payload too short')

    return { kid, nonce, payload }
}

export async function seal(key, kid, plaintext, aad, nonce) {
    const iv = nonce ?? randomNonce()
    if (iv.length !== NONCE_LENGTH) throw new TransportError(ERR_MALFORMED, 'nonce length')

    const sealed = await getSubtle().encrypt(
        { name: 'AES-GCM', iv, additionalData: aad, tagLength: TAG_LENGTH_BITS },
        key,
        plaintext,
    )

    return `${MAGIC}.${kid}.${b64urlEncode(iv)}.${b64urlEncode(new Uint8Array(sealed))}`
}

export async function unseal(key, envelope, aad) {
    const { nonce, payload } =
        typeof envelope === 'string' ? decodeEnvelope(envelope) : envelope
    try {
        const opened = await getSubtle().decrypt(
            { name: 'AES-GCM', iv: nonce, additionalData: aad, tagLength: TAG_LENGTH_BITS },
            key,
            payload,
        )
        return new Uint8Array(opened)
    } catch {
        throw new TransportError(ERR_DECRYPT_FAILED, 'decryption failed')
    }
}

// ---------------------------------------------------------------------------
// The JSON wrapper — WIRE_FORMAT.md §2
// ---------------------------------------------------------------------------

export function wrapBody(envelope) {
    return { d: envelope }
}

export function unwrapBody(body) {
    if (typeof body !== 'object' || body === null) {
        throw new TransportError(ERR_MALFORMED, 'wrapper not an object')
    }
    const keys = Object.keys(body)
    if (keys.length !== 1 || keys[0] !== 'd') {
        throw new TransportError(ERR_MALFORMED, 'wrapper members')
    }
    const envelope = body.d
    if (typeof envelope !== 'string') {
        throw new TransportError(ERR_MALFORMED, 'd not a string')
    }
    return envelope
}

export function looksEnveloped(body) {
    if (typeof body !== 'object' || body === null) return false
    const keys = Object.keys(body)
    if (keys.length !== 1 || keys[0] !== 'd') return false
    const envelope = body.d
    return typeof envelope === 'string' && envelope.startsWith(`${MAGIC}.`)
}

// ---------------------------------------------------------------------------
// Message ids
// ---------------------------------------------------------------------------

export function newMessageId() {
    const provider = getCrypto()
    if (typeof provider.randomUUID === 'function') {
        return provider.randomUUID()
    }
    const bytes = provider.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function nowSeconds() {
    return Math.floor(Date.now() / 1000)
}
